import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { Container, Grid, Typography, Button, Divider, Hidden, InputBase } from '@material-ui/core';

import { useWkblSpecialVideo } from 'hooks';
import { RootState } from 'redux/reducers/type';
import { CancelIcon, SearchIcon } from 'asset/images';

import CodeVO from 'commons/vo/CodeVO';
import WkblVideoParamVO from 'commons/vo/param/WkblVideoParamVO';

import { useStyles } from 'components/SearchFilter';
import { SpecialGameTypeSelect, SeasonSelect } from 'components/Select';
import { FromToDate } from 'components/Date';
import { HtmlTooltip } from 'components/Tooltip';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

function SearchFilterBySpecial() {
  const classes = useStyles();
  const { wkblSpecialGameTypeList: gameTypeList, wkblSeasonList: seasonList } = useSelector(
    (state: RootState) => state.code,
  );
  const { pagination, specialVideoList, searchCondition } = useSelector(
    (state: RootState) => state.wkblSpecialVideo,
  );
  const { onRequestWkblSepcialVideoList, onChangeWkblSpecialVideoPage } = useWkblSpecialVideo();

  const [keywords, setKeywords] = useState<string>('');
  const [season, setSeason] = useState<string>('all');
  const [gameTypes, setGameTypes] = useState<number[]>([]);

  const [gameDateFrom, setGameDateFrom] = useState<Date | null>(null);
  const [gameDateTo, setGameDateTo] = useState<Date | null>(null);

  // 가리기 - 보이기 토글버튼
  const [isShowSeason, setIsShowSeason] = useState<boolean>(false);
  const [isShowGameType, setIsShowGameType] = useState<boolean>(false);

  const formattedGameDateFrom = useMemo(() => {
    if (gameDateFrom) {
      const year = gameDateFrom.getFullYear();
      const month = gameDateFrom.getMonth() + 1;
      const day = gameDateFrom.getDate();
      const result = `${year}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}`;
      return result;
    } else {
      return '';
    }
  }, [gameDateFrom]);

  const formattedGameDateTo = useMemo(() => {
    if (gameDateTo !== null) {
      const year = gameDateTo.getFullYear();
      const month = gameDateTo.getMonth() + 1;
      const day = gameDateTo.getDate();
      const result = `${year}-${month < 10 ? '0' + month : month}-${day < 10 ? '0' + day : day}`;
      return result;
    } else {
      return '';
    }
  }, [gameDateTo]);

  const gameDateFromParam = useMemo(() => {
    const result = formattedGameDateFrom.replace(/-/g, '');
    if (result.length === 0) {
      return undefined;
    }
    return result;
  }, [formattedGameDateFrom]);

  const gameDateToParam = useMemo(() => {
    const result = formattedGameDateTo.replace(/-/g, '');
    if (result.length === 0) {
      return undefined;
    }
    return result;
  }, [formattedGameDateTo]);

  const search = (pageNumber: number) => {
    const searchParam = new WkblVideoParamVO({
      searchType: 'ETC',
      season,
      gameTypes,
      gameDateFrom: gameDateFromParam,
      gameDateTo: gameDateToParam,
      keywords: keywords ? [keywords] : [],
      page: pageNumber,
      retr: searchCondition.retr,
      sort: searchCondition.sort,
      asc: searchCondition.asc,
    });
    onRequestWkblSepcialVideoList(searchParam);
  };

  useEffect(() => {
    search(searchCondition.page);
  }, [searchCondition.page]);

  useEffect(() => {
    onClickSearch();
  }, [
    season,
    gameTypes,
    gameDateFrom,
    gameDateTo,
    searchCondition.retr,
    searchCondition.asc,
    searchCondition.sort,
  ]);

  useEffect(() => {
    // 없는 페이지 번호로 호출시 1페이지로 재검색 (1페이지를 요청하지 않은 경우)
    if (searchCondition.page > 0 && searchCondition.page + 1 > pagination.pageCount) {
      onChangeWkblSpecialVideoPage(0);
    }
  }, [pagination]);

  const onClickSearch = () => {
    if (searchCondition.page === 0) {
      search(0);
    } else {
      onChangeWkblSpecialVideoPage(0);
    }
  };

  const onClickSeason = useCallback(event => {
    const { value } = event.currentTarget;
    setSeason(value);
  }, []);

  const onClickGameType = useCallback(
    event => {
      const { value } = event.currentTarget;
      const param: number[] = [...gameTypes];
      const parseValue = parseInt(value);

      const index = param.indexOf(parseValue);
      if (index !== -1) {
        const first = param.slice(0, index);
        const second = param.slice(index + 1);
        setGameTypes([...first, ...second]);
      } else {
        param.push(parseValue);
        setGameTypes(param);
      }
    },
    [gameTypes],
  );

  const onSelectGameType = useCallback(
    value => {
      const param: number[] = [...gameTypes];
      const parseValue = parseInt(value);

      const index = param.indexOf(parseValue);
      if (index !== -1) {
        const first = param.slice(0, index);
        const second = param.slice(index + 1);
        setGameTypes([...first, ...second]);
      } else {
        param.push(parseValue);
        setGameTypes(param);
      }
    },
    [gameTypes],
  );

  const onChangeGameDateFrom = useCallback(
    (date: Date | null) => {
      if (date && gameDateTo && date > gameDateTo) {
        setGameDateTo(date);
      }
      setGameDateFrom(date);
    },
    [gameDateFrom, gameDateTo],
  );

  const onChangeGameDateTo = useCallback(
    (date: Date | null) => {
      if (date && gameDateFrom && date < gameDateFrom) {
        setGameDateFrom(date);
      }
      setGameDateTo(date);
    },
    [gameDateFrom, gameDateTo],
  );

  const onClearGameType = useCallback(() => {
    setGameTypes([]);
    setGameDateFrom(null);
    setGameDateTo(null);
    setSeason('all');
    setKeywords('');
  }, []);

  const onDeleteGameType = useCallback(
    (id: number) => {
      const param: number[] = [...gameTypes];
      const index = param.indexOf(id);
      const first = param.slice(0, index);
      const second = param.slice(index + 1);
      setGameTypes([...first, ...second]);
    },
    [gameTypes],
  );

  const onChangeGameType = (
    event: React.ChangeEvent<{ name?: string | undefined; value: unknown }>,
  ) => {
    const { value } = event.target;
    console.log(event, value, gameTypes);
    setGameTypes(value as number[]);
  };

  const onDeleteDateFrom = useCallback(() => {
    setGameDateFrom(null);
  }, [gameDateFrom]);

  const onDeleteDateTo = useCallback(() => {
    setGameDateTo(null);
  }, [gameDateTo]);

  const onChangeKeywords = useCallback((event: React.ChangeEvent<{ value: string }>) => {
    const { value } = event.currentTarget;
    setKeywords(value);
  }, []);

  const onSubmitSearch = useCallback(
    e => {
      e.preventDefault();

      onClickSearch();

      return false; // Block form forward
    },
    [keywords],
  );

  return (
    <Container component="article" className={classes.root}>
      {/*기록*/}
      <Grid container wrap="nowrap" className={classes.container}>
        <Grid item xs container justifyContent="center" className={classes.titleContainer}>
          <Typography className={classes.title}>시즌</Typography>
        </Grid>
        <Grid item xs className={classes.filterContainer}>
          <Hidden mdDown>
            <button
              className={clsx(classes.season, String(season) === 'all' && classes.selected)}
              value="all"
              onClick={onClickSeason}
            >
              모두
            </button>
            {(isShowSeason ? seasonList : seasonList.slice(0, 5)).map((seasonItem: CodeVO) => (
              <HtmlTooltip key={seasonItem.id} title={seasonItem.name} placement="bottom">
                <button
                  className={clsx(
                    classes.season,
                    String(seasonItem.id) === String(season) && classes.selected,
                  )}
                  value={seasonItem.id}
                  onClick={onClickSeason}
                >
                  {seasonItem.name}
                </button>
              </HtmlTooltip>
            ))}
          </Hidden>
          <Hidden lgUp>
            <SeasonSelect seasonId={season} seasonList={seasonList} onChangeSeason={setSeason} />
          </Hidden>
        </Grid>
        <Hidden mdDown>
          <Grid
            item
            onClick={() => {
              setIsShowSeason(!isShowSeason);
            }}
            style={{
              transform: `rotate(${isShowSeason ? 180 : 0}deg)`,
            }}
            className={classes.moreButton}
          >
            <ExpandMoreIcon />
          </Grid>
        </Hidden>
      </Grid>

      <Divider />

      {/*구분*/}
      <Grid container wrap="nowrap" className={classes.container}>
        <Grid item xs container justifyContent="center" className={classes.titleContainer}>
          <Typography className={classes.title}>구분</Typography>
        </Grid>
        <Grid item xs className={classes.filterContainer}>
          <Hidden mdDown>
            {(isShowGameType ? gameTypeList : gameTypeList.slice(0, 6)).map((gameType: CodeVO) => {
              const index = gameTypes.indexOf(gameType.id);
              const isSelected = index !== -1;
              return (
                <HtmlTooltip key={gameType.id} title={gameType.name} placement="bottom">
                  <button
                    className={clsx(classes.season, isSelected && classes.selected)}
                    value={gameType.id}
                    onClick={onClickGameType}
                  >
                    {gameType.name}
                  </button>
                </HtmlTooltip>
              );
            })}
          </Hidden>
          <Hidden lgUp>
            <SpecialGameTypeSelect
              gameTypes={gameTypeList}
              selectedGameTypes={gameTypes}
              onClickGameType={onSelectGameType}
            />
          </Hidden>
        </Grid>
        <Hidden mdDown>
          <Grid
            item
            onClick={() => {
              setIsShowGameType(!isShowGameType);
            }}
            style={{
              transform: `rotate(${isShowGameType ? 180 : 0}deg)`,
            }}
            className={classes.moreButton}
          >
            <ExpandMoreIcon />
          </Grid>
        </Hidden>
      </Grid>

      <Divider />

      {/*기간*/}
      <Grid container wrap="nowrap" className={classes.container}>
        <Grid item xs container justifyContent="center" className={classes.titleContainer}>
          <Typography className={classes.title}>기간</Typography>
        </Grid>
        <Grid item xs className={clsx(classes.filterContainer, classes.dateContainer)}>
          <FromToDate
            from={gameDateFrom}
            to={gameDateTo}
            onChangeFrom={onChangeGameDateFrom}
            onChangeTo={onChangeGameDateTo}
          />
        </Grid>
      </Grid>

      <Divider />

      {/*검색어*/}
      <Grid container className={classes.container}>
        <Grid item xs container justifyContent="center" className={classes.titleContainer}>
          <Typography className={classes.title}>검색어</Typography>
        </Grid>
        <Grid item xs className={clsx(classes.inputPlayerNameContainer, classes.filterContainer)}>
          <form onSubmit={onSubmitSearch}>
            <InputBase
              type="search"
              className={classes.inputPlayerName}
              placeholder="검색어를 입력해주세요."
              onChange={onChangeKeywords}
              value={keywords}
            />
          </form>
        </Grid>
        <Hidden xsDown>
          <Grid item xs className={classes.deselectionButtonContainer}>
            <Button className={classes.deselectionButton} onClick={onClearGameType}>
              <img src={CancelIcon}></img>
              <Typography className={classes.title}>선택해제</Typography>
            </Button>
          </Grid>
          <Grid
            item
            xs
            container
            justifyContent="flex-end"
            className={classes.searchButtonContainer}
          >
            <Button
              className={classes.searchButton}
              startIcon={<img src={SearchIcon} />}
              onClick={onClickSearch}
            >
              검색
            </Button>
          </Grid>
        </Hidden>
      </Grid>

      <Divider />

      {/*모바일에서 검색버튼*/}
      <Hidden smUp>
        <Grid container justifyContent="flex-end" className={clsx(classes.container)}>
          <Grid item xs className={classes.deselectionButtonContainer}>
            <Button className={classes.deselectionButton} onClick={onClearGameType}>
              <img src={CancelIcon}></img>
              <Typography className={classes.title}>선택해제</Typography>
            </Button>
          </Grid>
          <Grid
            item
            xs
            container
            justifyContent="flex-end"
            className={classes.searchButtonContainer}
          >
            <Button
              className={classes.searchButton}
              startIcon={<img src={SearchIcon} />}
              onClick={onClickSearch}
            >
              검색
            </Button>
          </Grid>
        </Grid>
      </Hidden>
    </Container>
  );
}

export default SearchFilterBySpecial;
